import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Row,
  Col,
  Card,
  Spin,
  Alert,
  Tag,
  Typography,
  Divider,
  Empty,
  Button
} from 'antd';
import {
  ShopOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined,
  ArrowLeftOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import partnerService from '../../services/partnerService';
import BackButton from '../../components/BackButton';
import FavoriteButton from '../../components/favorites/FavoriteButton';
import FoundingPartnerBadge from '../../components/badges/FoundingPartnerBadge';
import { getCityByLanguage, formatCityCountry } from '../../utils/locationFormat';

const { Title, Text, Paragraph } = Typography;

const PartnerProfilePage = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPartner();
  }, [id]);

  const loadPartner = async () => {
    setLoading(true);
    setError(null);

    const result = await partnerService.fetchPartnerById(id);

    if (result.success) {
      setPartner(result.data);
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  if (loading) {
    return (
      <div style={{ padding: '48px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !partner) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message={t('partners.profile.notFound.title')}
          description={error || t('partners.profile.notFound.description')}
          type="error"
          showIcon
        />
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          style={{ marginTop: 16 }}
        >
          {t('partners.profile.goBack')}
        </Button>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: 1200, margin: '0 auto' }}>
      <BackButton label={t('partners.profile.goBack')} />

      {/* Partner Header Card */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[24, 24]} align="middle">
          <Col xs={24} md={4}>
            <div
              style={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                backgroundColor: '#9FB18C',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto'
              }}
            >
              <ShopOutlined style={{ fontSize: 48, color: '#fff' }} />
            </div>
          </Col>
          <Col xs={24} md={20}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
                <Title level={2} style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
                  {partner.store_name}
                  {partner.segment === 'FOUNDING' && (
                    <FoundingPartnerBadge size="large" />
                  )}
                  {partner.is_verified && (
                    <Tag
                      color="green"
                      icon={<SafetyCertificateOutlined />}
                      style={{ marginLeft: 4, verticalAlign: 'middle' }}
                    >
                      {t('partners.profile.verifiedPartner')}
                    </Tag>
                  )}
                </Title>
                <FavoriteButton
                  type="partner"
                  itemId={partner.id}
                  showText
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#666' }}>
                <EnvironmentOutlined />
                <Text>{formatCityCountry(partner.city, partner.country, i18n.language)}</Text>
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Partner Info */}
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card title={t('partners.profile.about')} style={{ height: '100%' }}>
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">{t('partners.profile.location')}</Text>
              <Paragraph style={{ marginTop: 4 }}>
                <EnvironmentOutlined style={{ marginRight: 8 }} />
                {getCityByLanguage(partner.city, i18n.language)}{partner.postal_code && `, ${partner.postal_code}`}, {getCityByLanguage(partner.country, i18n.language)}
              </Paragraph>
            </div>

            {partner.is_verified && (
              <div>
                <Tag icon={<CheckCircleOutlined />} color="success">
                  {t('partners.profile.verifiedBusiness')}
                </Tag>
                <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                  {t('partners.profile.verifiedHelp')}
                </Text>
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title={t('partners.profile.quickLinks')} style={{ height: '100%' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Button
                type="primary"
                block
                onClick={() => navigate(`/services?partner=${id}`)}
              >
                {t('partners.profile.viewServices')}
              </Button>
              <Button
                block
                onClick={() => navigate(`/products?partner=${id}`)}
              >
                {t('partners.profile.viewProducts')}
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Map placeholder - can be enhanced later */}
      {partner.has_location && partner.latitude && partner.longitude && (
        <Card title={t('partners.profile.location')} style={{ marginTop: 24 }}>
          <div
            style={{
              height: 200,
              backgroundColor: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 8
            }}
          >
            <Text type="secondary">
              {t('partners.profile.mapComingSoon')}
            </Text>
          </div>
        </Card>
      )}
    </div>
  );
};

export default PartnerProfilePage;
